import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import 'primeflex/primeflex.css';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import {
  Widget,
  addResponseMessage,
  renderCustomComponent,
  addUserMessage,
  deleteMessages,
  toggleWidget
} from 'react-chat-widget';
import 'react-chat-widget/lib/styles.css';
import { Toast } from 'primereact/toast';
import { ModalInstall } from './ModalInstall';
import { ModalPassword } from './ModalPassword';
import * as XLSX from 'xlsx';
import { DataTable } from 'primereact/datatable';
import { Column }    from 'primereact/column';

const Main = () => {const location = useLocation();
  const [password, setPassword] = useState(localStorage.getItem('backend_password'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [listening, setListening] = useState(false);
  const [animate, setAnimate] = useState(false);
  const toast = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    toggleWidget();
  }, []);

  // Efecto para manejar cambios en la autenticación
  useEffect(() => {
    if (isAuthenticated) {
      const currentPassword = localStorage.getItem('backend_password');
      setPassword(currentPassword);
    }
  }, [isAuthenticated]);

  const speak = (text) => {
    if ('speechSynthesis' in window) {
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = 'es-ES';
      utter.rate = 1.5;
      window.speechSynthesis.speak(utter);
    }
  };

  async function parseExcelToJson(blob) {
    const arrayBuffer = await blob.arrayBuffer();
    const workbook     = XLSX.read(arrayBuffer, { type: 'array' });
    const sheetName    = workbook.SheetNames[0];
    const worksheet    = workbook.Sheets[sheetName];
  
    // Obtén un array de arrays (cada fila es un array)
    const raw = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    const headers = raw[0] || [];
    const rows = raw.slice(1).map(row => {
      const obj = {};
      headers.forEach((h, i) => (obj[h] = row[i]));
      return obj;
    });
  
    return { headers, rows };
  }  

  const toggleListening = () => {
    if (listening) {
      recognitionRef.current.stop();
      handleAnimationEnd();
    } else {
      recognitionRef.current.start();
      setListening(true);
    }
  };

  // Función para descargar el archivo
  const downloadFile = async (filename) => {
    try {
      const url_server_local = 'https://olarra.ager-agro.es';
      const response = await fetch(url_server_local + '/api_olarra_chatbot/get_file', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ password, filename })
      });
      if (!response.ok) {
        throw new Error('Error al descargar el archivo');
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Error al descargar el archivo', error);
      toast.current.show({
        severity: 'error',
        summary: 'Descarga fallida',
        detail: 'No se pudo descargar el archivo',
        life: 3000
      });
    }
  };

  // Asignamos la función downloadFile al objeto window para que sea accesible desde el onclick inline
  useEffect(() => {
    window.downloadFile = downloadFile;
  }, [downloadFile]);

  const checkConnection = async () => {
    try {
      await fetch('https://www.google.com?no-sw=true', { mode: 'no-cors' });
    } catch (error) {
      toast.current.show({
        severity: 'error',
        summary: 'Error',
        detail: 'No tienes conexión a Internet',
        life: 3000
      });
    }
  };

  // useEffect para borrar mensajes previos, chequear conexión, abrir el widget y dar la bienvenida
  useEffect(() => {
    // 1. Borrar mensajes al iniciar
    deleteMessages();

    // 2. Revisar conexión
    checkConnection();

    // 4. Mensaje de bienvenida si no hay historial
    addResponseMessage('¡Bienvenido!, ¿en qué te puedo ayudar?');
  }, []);

  const openUrlAutomatically = (text) => {
    const urlPattern = /(https?:\/\/[^\s]+)/g;
    const match = urlPattern.exec(text);
    if (match) {
      window.open(match[0], '_blank');
    }
  };

  const handleNewUserMessage = useCallback(async (question) => {
    // Evitar que el usuario envíe otra pregunta mientras se procesa
    if (isProcessing) {
      toast.current.show({
        severity: 'error',
        summary: 'Procesando',
        detail: 'Por favor, espera a que se termine de procesar la respuesta.',
        life: 3000
      });
      // Borrar el último mensaje enviado por el usuario
      deleteMessages(1);

      // Opcional: Devolver el texto al input
      const inputElement = document.querySelector('.rcw-input');
      if (inputElement) {
        setTimeout(() => {
          inputElement.innerText = question;
        }, 50);
      }
      return;
    }

    setIsProcessing(true);
    try {
      const messagesContainer = document.querySelector('.rcw-messages-container');
      let loadingDiv;
      if (messagesContainer) {
        loadingDiv = document.createElement('div');
        loadingDiv.className = 'loading-container';

        const loadingChatDiv = document.createElement('div');
        loadingChatDiv.className = 'loading-chat';

        loadingDiv.appendChild(loadingChatDiv);
        messagesContainer.appendChild(loadingDiv);
      }

      const inputElement = document.querySelector('.rcw-send');
      if (inputElement) {
        inputElement.focus();
        inputElement.blur();
      }

      const url_server_local = 'https://olarra.ager-agro.es';
      const response = await fetch(url_server_local + '/api_olarra_chatbot/ask', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ question, type: 'ventas', thread_id: threadId, password: password }),
        cache: 'no-store'
      });

      if (!response.ok) {
        throw new Error('Error en la respuesta de la API');
      } else if (loadingDiv) {
        messagesContainer.removeChild(loadingDiv);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder('utf-8');
      let completeResponse = '';
      let botResponse = '';
      let scrollCount = 0;

      // Agregar un mensaje inicial vacío para actualizarlo con la respuesta en stream
      addResponseMessage('');

      // Leer el stream de la respuesta de forma progresiva
      await new Promise((resolve) => {
        setTimeout(() => {
          const messages = document.querySelectorAll('.rcw-message-text');
          const lastMessageElement = messages[messages.length - 1];
          let bufferText = '';
          (async () => {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              const chunk = decoder.decode(value, { stream: true });
              completeResponse += chunk;

              // Si existe "historial:", cortamos la respuesta antes de eso
              const markerIndex = completeResponse.indexOf('\nhistorial: ');
              let displayResponse = completeResponse;
              if (markerIndex !== -1) {
                displayResponse = completeResponse.substring(0, markerIndex);
              }

              // Formatear para negritas y encabezados
              const formattedResponse = displayResponse
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/"([^"]+)"/g, '<strong>$1</strong>')
                .replace(/`([^`]+)`/g, '<strong>$1</strong>')
                .replace(/### (.*?)\n/g, '<h3>$1</h3>\n')
                .replace(/\[Descargar.*?\]\(sandbox:\/mnt\/data\/([^\/\)]+)\)/g, '<a href="#" style="color: blue; text-decoration: underline;" onclick="downloadFile(\'$1\')">$1</a>');

              bufferText += chunk;  // acumula el texto sin HTML
              const sentenceEndPattern = /([^\.!\?\n]+[\.!\?\n]+)/g;
              let m;
              while ((m = sentenceEndPattern.exec(bufferText))) {
                const sentence = m[1].replace(/\n/g,' ').trim();
                //if (sentence) speak(sentence);
              }
              bufferText = bufferText.replace(sentenceEndPattern, '');

              if (lastMessageElement) {
                lastMessageElement.innerHTML = formattedResponse;
                if (scrollCount < 100) {
                  const messagesContainer = document.querySelector('.rcw-messages-container');
                  if (messagesContainer) {
                    messagesContainer.scrollTop = messagesContainer.scrollHeight;
                    scrollCount++;
                  }
                }
              }
            }
            resolve();
          })();
        }, 100);
      });

      setThreadId(response.headers.get('X-Thread-Id'));

      const messages = document.querySelectorAll('.rcw-message-text');
      const lastMessageEl = messages[messages.length - 1];

      const html = lastMessageEl.innerHTML;
      const downloadLinkMatch = html.match(/onclick="downloadFile\('([^']+)'\)"/);

      if (downloadLinkMatch) {
        const filename = downloadLinkMatch[1];
      
        // Descarga el blob
        const blobResp = await fetch(url_server_local + '/api_olarra_chatbot/get_file', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password, filename })
        });
        const blob = await blobResp.blob();
      
        // Parsear a JSON
        const { headers, rows } = await parseExcelToJson(blob);
      
        // Renderizar DataTable
        renderCustomComponent(
          () => (
            <div style={{ overflowX: 'auto', maxWidth: '100%', marginTop: '0.5rem', borderRadius: '10px', backgroundColor: '#3d0090' }}>
              <DataTable
                value={rows}
                paginator
                rows={10}
                stripedRows
                style={{ background: 'white', borderRadius: '6px' }}
              >
                {headers.map((field) => (
                  <Column
                    key={field}
                    field={field}
                    header={field}
                    sortable
                    filter
                    headerStyle={{ backgroundColor: '#3d0090', color: '#fff' }}
                  />
                ))}
              </DataTable>
            </div>
          ),
          { align: 'left' }
        );
      }
    } catch (error) {
      console.error('Error al obtener respuesta de ChatGPT:', error);
    } finally {
      setIsProcessing(false);
    }
  }, [threadId, password, isProcessing]);

  // Iniciar SpeechRecognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.current.show({
        severity: 'warn',
        summary: 'Voice not supported',
        detail: 'Tu navegador no soporta reconocimiento de voz',
        life: 3000
      });
      return;
    }
    const recognition = new SpeechRecognition();
    recognition.lang = 'es-ES';
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      addUserMessage(transcript);
      handleNewUserMessage(transcript);
    };
    recognition.onend = () => {
      setListening(false);
      handleAnimationEnd();
    };
    recognitionRef.current = recognition;

    // Cleanup: detenemos la instancia anterior antes de crear otra
    return () => {
      recognition.stop();
    };
  }, [handleNewUserMessage]);

  const handleClickMicro = () => {
    // Lanzamos la animación
    setAnimate(true);
    // Y disparamos tu lógica de start/stop
    toggleListening();
  };

  const handleAnimationEnd = () => {
    // Limpiamos para poder volver a animar en el siguiente click
    setAnimate(false);
  };

  return (
    <>
      <ModalPassword 
        isAuthenticated={isAuthenticated} 
        setIsAuthenticated={setIsAuthenticated} 
      />
      {isAuthenticated && (
        <>
          <ModalInstall />
          <div className="body-div flex flex-column align-items-center">
        {/* Flecha para volver hacia atrás (posición absoluta) */}
        <i
          className="pi pi-arrow-left"
          style={{
            position: 'absolute',
            top: '15px',
            left: '15px',
            fontSize: '1.5rem',
            cursor: 'pointer',
            zIndex: 500
          }}
          onClick={() => window.history.back()}
        />
        <Toast ref={toast} />
        <div style={{ 
          position: 'absolute', 
          bottom: window.innerWidth > 800 ? '17px' : '23px', 
          right: window.innerWidth > 360 ? '18px' : window.innerWidth > 320 ? '22%' : '16%', 
          zIndex: 99999 
        }}>
        <i
            className={`
              pi pi-microphone
              ${listening ? 'listening' : ''}
              ${animate   ? 'pulse'     : ''}
            `}
            style={{
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: listening ? 'red' : '#48A6FF'
            }}
            onClick={handleClickMicro}
          />
        </div>
        <Widget
          handleNewUserMessage={handleNewUserMessage}
          subtitle={null}
          emojis={true}
          title={'Bodegas Olarra'}
          showTimeStamp={false}
          showCloseButton={false}
          fullScreenMode={true}
          senderPlaceHolder={'Escribe tu mensaje...'}
          style={{ maxWidth: '100%', margin: '0 auto' }}
        />
      </div>
        </>
      )}
    </>
  );
};


export default Main;
